import { t, dateLocale } from '../../i18n';

const PPTX_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pptxgenjs/3.12.0/pptxgen.bundle.js';
const COLORS = ['5B5CE6', '22C55E', 'F59E0B', 'EF4444', '06B6D4', 'A855F7', 'EC4899', '84CC16'];
const NAVY = '11142B';
const nf = (n) => new Intl.NumberFormat(dateLocale).format(n || 0);
const pct = (part, total) => (total > 0 ? `${Math.round((part / total) * 1000) / 10} %` : '—');
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' });

// pptxgenjs est chargé à la demande depuis un CDN (aucune dépendance ajoutée à l'application).
function loadPptxGen() {
  if (window.PptxGenJS) return Promise.resolve(window.PptxGenJS);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = PPTX_CDN;
    s.onload = () => (window.PptxGenJS ? resolve(window.PptxGenJS) : reject(new Error('pptxgenjs')));
    s.onerror = () => reject(new Error('pptxgenjs'));
    document.head.appendChild(s);
  });
}

// Présentation commerciale reprenant toutes les statistiques de la page : graphiques PowerPoint natifs (modifiables).
export async function exportStatsPptx({ projectName, periodLabel, data }) {
  const PptxGenJS = await loadPptxGen();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE'; // 13.33 x 7.5 in
  pptx.title = `${t('ui.stats.title')} — ${projectName}`;

  const { totals, days, devices, flows } = data;
  const pages = data.pages.filter((p) => p.clicks > 0 || p.views > 0)
    .map((p) => (p.page === '—' ? { ...p, page: t('ui.stats.unknown_page'), unknown: true } : p));
  const clickPages = pages.filter((p) => p.clicks > 0);
  const named = clickPages.filter((p) => !p.unknown);

  const slide = (title) => {
    const s = pptx.addSlide();
    s.background = { color: 'FFFFFF' };
    s.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 13.33, h: 0.9, fill: { color: NAVY } });
    s.addText(title, { x: 0.5, y: 0.1, w: 12.3, h: 0.7, fontSize: 24, bold: true, color: 'FFFFFF', fontFace: 'Calibri' });
    s.addText(`${projectName} · ${periodLabel}`, { x: 0.5, y: 7.05, w: 12.3, h: 0.3, fontSize: 10, color: '888888' });
    return s;
  };

  // 1. Titre
  const cover = pptx.addSlide();
  cover.background = { color: NAVY };
  cover.addText(t('ui.stats.title'), { x: 0.8, y: 2.3, w: 11.7, h: 1, fontSize: 48, bold: true, color: 'FFFFFF' });
  cover.addText(projectName, { x: 0.8, y: 3.4, w: 11.7, h: 0.7, fontSize: 28, color: 'C7C9F5' });
  cover.addText(`${periodLabel} · ${new Date().toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}`, { x: 0.8, y: 4.2, w: 11.7, h: 0.5, fontSize: 16, color: '94A3B8' });

  // 2. Chiffres clés
  const kpi = slide(t('ui.stats.kpis_title'));
  const perSession = totals.sessions > 0 ? String(Math.round((totals.clicks / totals.sessions) * 10) / 10) : '—';
  [[nf(totals.clicks), t('ui.stats.clicks')], [nf(totals.views), t('ui.stats.views')], [perSession, t('ui.stats.clicks_per_session')],
    [nf(totals.sessions), t('ui.stats.sessions')], [nf(totals.devices), t('ui.stats.screens')]].forEach(([value, label], i) => {
    const x = 0.5 + (i % 3) * 4.2; const y = 1.4 + Math.floor(i / 3) * 2.6;
    kpi.addShape(pptx.ShapeType.roundRect, { x, y, w: 3.9, h: 2.3, fill: { color: 'F1F5F9' }, rectRadius: 0.15 });
    kpi.addText(value, { x, y: y + 0.3, w: 3.9, h: 1.1, fontSize: 48, bold: true, color: '5B5CE6', align: 'center' });
    kpi.addText(label, { x, y: y + 1.4, w: 3.9, h: 0.6, fontSize: 18, color: '475569', align: 'center' });
  });

  // 3. Points à retenir
  const byRate = pages.filter((p) => !p.unknown && p.views >= 5).map((p) => ({ ...p, rate: p.clicks / p.views })).sort((a, b) => b.rate - a.rate)[0];
  const quiet = pages.filter((p) => !p.unknown && p.views >= 5).sort((a, b) => a.clicks / a.views - b.clicks / b.views)[0];
  const peak = days.length ? days.reduce((a, b) => (b.clicks > a.clicks ? b : a)) : null;
  const insights = [];
  if (named[0]) insights.push(t('ui.stats.insight_top', { page: named[0].page, share: pct(named[0].clicks, totals.clicks) }));
  if (byRate) insights.push(t('ui.stats.insight_rate', { page: byRate.page, rate: pct(byRate.clicks, byRate.views) }));
  if (quiet && (!byRate || quiet.page !== byRate.page)) insights.push(t('ui.stats.insight_quiet', { page: quiet.page, rate: pct(quiet.clicks, quiet.views) }));
  if (peak && peak.clicks > 0) insights.push(t('ui.stats.insight_peak', { day: fmtDay(peak.day), n: nf(peak.clicks) }));
  if (insights.length) {
    const s = slide(t('ui.stats.insights_title'));
    s.addText(insights.map((text) => ({ text, options: { bullet: true, breakLine: true } })), { x: 0.7, y: 1.4, w: 11.9, h: 5, fontSize: 22, color: '1E293B', paraSpaceAfter: 14, valign: 'top' });
  }

  // 4. Répartition des clics (camembert)
  if (clickPages.length) {
    const s = slide(t('ui.stats.share_of_clicks'));
    const top = clickPages.slice(0, 7);
    const rest = clickPages.slice(7).reduce((sum, p) => sum + p.clicks, 0);
    const labels = top.map((p) => p.page); const values = top.map((p) => p.clicks);
    if (rest > 0) { labels.push(t('ui.stats.others')); values.push(rest); }
    s.addChart(pptx.ChartType.doughnut, [{ name: t('ui.stats.clicks'), labels, values }], {
      x: 0.5, y: 1.2, w: 7, h: 5.7, chartColors: [...COLORS.slice(0, top.length), '94A3B8'], showLegend: true, legendPos: 'b', legendFontSize: 12,
      showPercent: true, dataLabelColor: 'FFFFFF', dataLabelFontSize: 12, holeSize: 55,
    });
    s.addTable([[{ text: t('ui.stats.page'), options: { bold: true } }, { text: t('ui.stats.clicks'), options: { bold: true } }, { text: t('ui.stats.share'), options: { bold: true } }],
      ...top.map((p) => [p.page, nf(p.clicks), pct(p.clicks, totals.clicks)])],
    { x: 7.8, y: 1.6, w: 5, colW: [2.6, 1, 1.4], fontSize: 13, border: { type: 'solid', pt: 0.5, color: 'E2E8F0' }, color: '1E293B' });
  }

  // 5. Comparaison des pages (vues vs clics)
  if (pages.length) {
    const s = slide(t('ui.stats.compare_pages'));
    const p10 = pages.slice(0, 10);
    s.addChart(pptx.ChartType.bar, [
      { name: t('ui.stats.views'), labels: p10.map((p) => p.page), values: p10.map((p) => p.views) },
      { name: t('ui.stats.clicks'), labels: p10.map((p) => p.page), values: p10.map((p) => p.clicks) },
    ], { x: 0.5, y: 1.2, w: 12.3, h: 5.7, barDir: 'col', chartColors: ['CBD5E1', '5B5CE6'], showLegend: true, legendPos: 'b', showValue: true, dataLabelFontSize: 11, catAxisLabelFontSize: 12, valAxisLabelFontSize: 11 });
  }

  // 6. Évolution par jour (dates précisées)
  if (days.length) {
    const s = slide(t('ui.stats.per_day'));
    const labels = days.map((d) => fmtDay(d.day));
    s.addChart(pptx.ChartType.bar, [
      { name: t('ui.stats.views'), labels, values: days.map((d) => d.views) },
      { name: t('ui.stats.clicks'), labels, values: days.map((d) => d.clicks) },
    ], { x: 0.5, y: 1.2, w: 12.3, h: 5.2, barDir: 'col', chartColors: ['CBD5E1', '5B5CE6'], showLegend: true, legendPos: 'b', catAxisLabelFontSize: 10, valAxisLabelFontSize: 11, catAxisLabelRotate: days.length > 14 ? 270 : 0 });
    if (peak && peak.clicks > 0) s.addText(t('ui.stats.insight_peak', { day: fmtDay(peak.day), n: nf(peak.clicks) }), { x: 0.5, y: 6.45, w: 12.3, h: 0.5, fontSize: 14, color: '475569' });
  }

  // 7. Détail par page
  if (pages.length) {
    const s = slide(t('ui.stats.table'));
    const head = [t('ui.stats.page'), t('ui.stats.views'), t('ui.stats.clicks'), t('ui.stats.click_rate'), t('ui.stats.share')]
      .map((text) => ({ text, options: { bold: true, color: 'FFFFFF', fill: { color: '5B5CE6' } } }));
    s.addTable([head, ...pages.slice(0, 14).map((p) => [p.page, nf(p.views), nf(p.clicks), p.views ? pct(p.clicks, p.views) : '—', pct(p.clicks, totals.clicks)])],
      { x: 0.5, y: 1.2, w: 12.3, colW: [5, 1.8, 1.8, 1.9, 1.8], fontSize: 13, border: { type: 'solid', pt: 0.5, color: 'E2E8F0' }, color: '1E293B', align: 'left' });
  }

  // 8. Écrans
  if (devices.length > 1) {
    const s = slide(t('ui.stats.compare_screens'));
    const d = devices.slice(0, 10);
    s.addChart(pptx.ChartType.bar, [{ name: t('ui.stats.clicks'), labels: d.map((x) => x.device), values: d.map((x) => x.clicks) }],
      { x: 0.5, y: 1.2, w: 12.3, h: 5.7, barDir: 'bar', chartColors: ['5B5CE6'], showValue: true, catAxisLabelFontSize: 12, showLegend: false });
  }

  // 9. Parcours
  if (flows.length) {
    const s = slide(t('ui.stats.flows'));
    const f = flows.slice(0, 10);
    s.addChart(pptx.ChartType.bar, [{ name: t('ui.stats.clicks'), labels: f.map((x) => `${x.from_page} → ${x.to_page}`), values: f.map((x) => x.count) }],
      { x: 0.5, y: 1.2, w: 12.3, h: 5.7, barDir: 'bar', chartColors: ['22C55E'], showValue: true, catAxisLabelFontSize: 12, showLegend: false });
  }

  const safe = String(projectName).replace(/[^\w\-]+/g, '_');
  await pptx.writeFile({ fileName: `stats_${safe}_${new Date().toISOString().slice(0, 10)}.pptx` });
}
